import { useEffect, useRef, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Field, type LabeledProps } from "./Field";
import { formatMoneyPlain, parseMoney } from "../lib/money";
import { docError, maskDoc, normalizeDoc, type DocKind } from "../lib/documents";
import { maskPhoneBr, phoneError, toE164Br } from "../lib/phone";

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "size">;

// ---------------------------------------------------------------- texto

export interface TextInputProps extends LabeledProps, Omit<NativeInput, "name" | "disabled" | "required"> {
  value: string;
  onChange: (value: string) => void;
}

export function TextInput({ label, hint, error, required, className, value, onChange, ...rest }: TextInputProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className="vx-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          {...rest}
        />
      )}
    </Field>
  );
}

export interface TextAreaProps extends LabeledProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  maxLength?: number;
  placeholder?: string;
}

export function TextArea({ label, hint, error, required, className, value, onChange, rows = 3, ...rest }: TextAreaProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className="vx-textarea"
          value={value}
          rows={rows}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          required={required}
          {...rest}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- select

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> extends LabeledProps {
  value: V | "";
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
}

export function Select<V extends string = string>({
  label, hint, error, required, className, value, onChange, options, placeholder, disabled, name,
}: SelectProps<V>) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          name={name}
          className="vx-select"
          value={value}
          disabled={disabled}
          required={required}
          onChange={(e) => onChange(e.target.value as V)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        >
          {placeholder !== undefined ? (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          ) : null}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- data

export interface DateInputProps extends LabeledProps {
  /** "AAAA-MM-DD" ou "" */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  type?: "date" | "month";
}

/** ISO "AAAA-MM-DD" → "dd/mm/aaaa" (ou "mm/aaaa" para mês). */
function isoToText(iso: string, month: boolean): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return month ? `${m[2]}/${m[1]}` : `${m[3]}/${m[2]}/${m[1]}`;
}

function maskDate(raw: string, month: boolean): string {
  const d = raw.replace(/\D/g, "").slice(0, month ? 6 : 8);
  if (month) return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  if (d.length > 4) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return d;
}

/** "dd/mm/aaaa" completo e válido → ISO; senão null. */
export function parseDateBr(text: string, month = false): string | null {
  const m = month ? /^(\d{2})\/(\d{4})$/.exec(text) : /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text);
  if (!m) return null;
  const [dd, mm, yyyy] = month ? ["01", m[1]!, m[2]!] : [m[1]!, m[2]!, m[3]!];
  const dt = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  if (dt.getUTCFullYear() !== Number(yyyy) || dt.getUTCMonth() !== Number(mm) - 1 || dt.getUTCDate() !== Number(dd)) return null;
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Data em pt-BR com máscara (dd/mm/aaaa ou mm/aaaa), independente do idioma do navegador.
 * Emite ISO "AAAA-MM-DD" (mês: dia 01) ou "" quando vazio.
 */
export function DateInput({ label, hint, error, required, className, value, onChange, min, max, disabled, name, type = "date" }: DateInputProps) {
  const month = type === "month";
  const [text, setText] = useState(() => isoToText(value, month));
  const [localError, setLocalError] = useState<string | null>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(isoToText(value, month));
  }, [value, month]);

  function check(t: string): string | null {
    if (t === "") return null;
    const iso = parseDateBr(t, month);
    if (!iso) return month ? "Use o formato mm/aaaa." : "Data inválida. Use dd/mm/aaaa.";
    if (min && iso < min) return `Mínimo: ${isoToText(min, month)}.`;
    if (max && iso > max) return `Máximo: ${isoToText(max, month)}.`;
    return null;
  }

  return (
    <Field label={label} hint={hint} error={error ?? localError} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={month ? "mm/aaaa" : "dd/mm/aaaa"}
          className="vx-input num"
          value={text}
          disabled={disabled}
          required={required}
          onFocus={() => {
            focused.current = true;
          }}
          onChange={(e) => {
            const t = maskDate(e.target.value, month);
            setText(t);
            setLocalError(null);
            if (t === "") onChange("");
            else {
              const iso = parseDateBr(t, month);
              if (iso && !check(t)) onChange(iso);
            }
          }}
          onBlur={() => {
            focused.current = false;
            setLocalError(check(text));
          }}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- hora

export interface TimeInputProps extends LabeledProps {
  /** "HH:MM" (24 h) ou "" */
  value: string;
  onChange: (value: string) => void;
}

function maskTime(raw: string): string {
  const d = raw.replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}:${d.slice(2)}` : d;
}

/** "HH:MM" válido (00:00 a 23:59) ou null. */
export function parseTime(text: string): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(text);
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return null;
  return text;
}

/** Hora de parede em 24 h com máscara, independente do idioma do navegador. Emite "HH:MM" ou "". */
export function TimeInput({ label, hint, error, required, className, value, onChange, disabled, name }: TimeInputProps) {
  const [text, setText] = useState(value);
  const [localError, setLocalError] = useState<string | null>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value);
  }, [value]);
  return (
    <Field label={label} hint={hint} error={error ?? localError} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="hh:mm"
          className="vx-input num"
          value={text}
          disabled={disabled}
          required={required}
          onFocus={() => {
            focused.current = true;
          }}
          onChange={(e) => {
            const t = maskTime(e.target.value);
            setText(t);
            setLocalError(null);
            if (t === "") onChange("");
            else if (parseTime(t)) onChange(t);
          }}
          onBlur={() => {
            focused.current = false;
            setLocalError(text !== "" && !parseTime(text) ? "Hora inválida. Use hh:mm (24 h)." : null);
          }}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- dinheiro

export interface MoneyInputProps extends LabeledProps {
  /** Centavos inteiros (o que vai para o banco como bigint) ou null. */
  value: number | null;
  onChange: (cents: number | null) => void;
  allowNegative?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  autoFocus?: boolean;
}

/**
 * Campo de dinheiro: a pessoa digita "1234,56", "1.234,56" ou "1234"; ao sair do campo o texto
 * vira "1.234,56". O valor emitido é sempre centavos inteiros (ou null quando vazio/inválido).
 */
export function MoneyInput({
  label, hint, error, required, className, value, onChange, allowNegative, placeholder = "0,00", min, max, disabled, name, autoFocus,
}: MoneyInputProps) {
  const [text, setText] = useState(() => formatMoneyPlain(value));
  const [localError, setLocalError] = useState<string | null>(null);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(formatMoneyPlain(value));
  }, [value]);

  function validate(cents: number | null, raw: string): string | null {
    if (raw.trim() !== "" && cents === null) return "Valor inválido. Use o formato 1.234,56.";
    if (cents !== null && cents < 0 && !allowNegative) return "O valor não pode ser negativo.";
    if (cents !== null && min !== undefined && cents < min) return `Mínimo: ${formatMoneyPlain(min)}.`;
    if (cents !== null && max !== undefined && cents > max) return `Máximo: ${formatMoneyPlain(max)}.`;
    return null;
  }

  return (
    <Field label={label} hint={hint} error={error ?? localError} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <div className="vx-input-group">
          <span className="vx-input-group__addon" aria-hidden="true">
            R$
          </span>
          <input
            id={id}
            name={name}
            className="vx-input vx-input--num"
            inputMode="decimal"
            autoComplete="off"
            autoFocus={autoFocus}
            placeholder={placeholder}
            value={text}
            disabled={disabled}
            required={required}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            onFocus={() => {
              focused.current = true;
            }}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9.,\-−R$ ]/g, "");
              setText(raw);
              const cents = parseMoney(raw);
              const err = validate(cents, raw);
              setLocalError(null);
              onChange(err ? null : cents);
            }}
            onBlur={() => {
              focused.current = false;
              const cents = parseMoney(text);
              const err = validate(cents, text);
              setLocalError(err);
              if (!err) setText(formatMoneyPlain(cents));
            }}
          />
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- CPF / CNPJ

export interface DocInputProps extends LabeledProps {
  /** Só dígitos (e letras no CNPJ alfanumérico). */
  value: string;
  onChange: (normalized: string, valid: boolean) => void;
  kind?: DocKind;
  placeholder?: string;
}

export function DocInput({ label, hint, error, required, className, value, onChange, kind, placeholder, disabled, name }: DocInputProps) {
  const [touched, setTouched] = useState(false);
  const local = touched ? docError(value, kind) : null;
  const ph = placeholder ?? (kind === "cpf" ? "000.000.000-00" : kind === "cnpj" ? "00.000.000/0000-00" : "CPF ou CNPJ");
  const maxLength = kind === "cpf" ? 14 : 18;
  return (
    <Field label={label} hint={hint} error={error ?? local} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          name={name}
          className="vx-input mono"
          value={maskDoc(value, kind)}
          placeholder={ph}
          maxLength={maxLength}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={(e) => {
            const n = normalizeDoc(e.target.value).slice(0, kind === "cpf" ? 11 : 14);
            onChange(n, n !== "" && docError(n, kind) === null);
          }}
          onBlur={() => setTouched(true)}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- telefone

export interface PhoneInputProps extends LabeledProps {
  /** Texto exibido/editado; use `toE164Br` para enviar. */
  value: string;
  onChange: (masked: string, e164: string | null, valid: boolean) => void;
}

export function PhoneInput({ label, hint, error, required, className, value, onChange, disabled, name }: PhoneInputProps) {
  const [touched, setTouched] = useState(false);
  const local = touched ? phoneError(value) : null;
  return (
    <Field label={label} hint={hint} error={error ?? local} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          name={name}
          type="tel"
          className="vx-input"
          value={maskPhoneBr(value)}
          placeholder="(71) 99999-0000"
          autoComplete="tel-national"
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onChange={(e) => {
            const masked = maskPhoneBr(e.target.value);
            onChange(masked, toE164Br(masked), phoneError(masked) === null);
          }}
          onBlur={() => setTouched(true)}
        />
      )}
    </Field>
  );
}

// ---------------------------------------------------------------- número / checkbox

export interface NumberInputProps extends LabeledProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: ReactNode;
}

export function NumberInput({ label, hint, error, required, className, value, onChange, min, max, step = 1, suffix, disabled, name }: NumberInputProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {({ id, describedBy, invalid }) => (
        <div className="vx-input-group">
          <input
            id={id}
            name={name}
            type="number"
            className="vx-input vx-input--num"
            value={value ?? ""}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            required={required}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          />
          {suffix ? (
            <span className="vx-input-group__addon vx-input-group__addon--end">
              {suffix}
            </span>
          ) : null}
        </div>
      )}
    </Field>
  );
}

export interface CheckboxProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  hint?: ReactNode;
}

export function Checkbox({ label, checked, onChange, disabled, hint }: CheckboxProps) {
  return (
    <label className="vx-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint ? <span className="vx-field__hint" style={{ display: "block" }}>{hint}</span> : null}
      </span>
    </label>
  );
}
